const normalizeHeader = (value, index) => String(value ?? "").trim() || `column_${index + 1}`;

export const parseCsv = (text = "", delimiter = ",") => {
    const source = String(text ?? "").replace(/^\uFEFF/, "");
    const rows = [];
    let row = [];
    let cell = "";
    let quoted = false;

    for (let index = 0; index < source.length; index += 1) {
        const char = source[index];
        const next = source[index + 1];
        if (char === '"') {
            if (quoted && next === '"') {
                cell += '"';
                index += 1;
            } else {
                quoted = !quoted;
            }
        } else if (char === delimiter && !quoted) {
            row.push(cell);
            cell = "";
        } else if ((char === "\n" || char === "\r") && !quoted) {
            if (char === "\r" && next === "\n") index += 1;
            row.push(cell);
            if (row.some((value) => String(value).length > 0)) rows.push(row);
            row = [];
            cell = "";
        } else {
            cell += char;
        }
    }
    row.push(cell);
    if (row.some((value) => String(value).length > 0)) rows.push(row);
    if (!rows.length) return [];

    const headers = rows[0].map(normalizeHeader);
    return rows.slice(1).map((values) => Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ""])));
};

export const rowsToCsv = (rows = [], delimiter = ",") => {
    if (!rows.length) return "";
    const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row || {}))));
    const quote = (value) => {
        const text = value == null ? "" : String(value);
        return /["\r\n,;]/.test(text) || text.includes(delimiter) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    return [headers.map(quote).join(delimiter), ...rows.map((row) => headers.map((key) => quote(row?.[key])).join(delimiter))].join("\n");
};

export const parseJsonRows = (text = "") => {
    const parsed = JSON.parse(String(text ?? "").trim() || "[]");
    if (Array.isArray(parsed)) return parsed.map((item) => (item && typeof item === "object" && !Array.isArray(item) ? item : { value: item }));
    if (parsed && typeof parsed === "object") {
        const candidate = Object.values(parsed).find((value) => Array.isArray(value));
        if (candidate) return candidate.map((item) => (item && typeof item === "object" && !Array.isArray(item) ? item : { value: item }));
        return [parsed];
    }
    return [{ value: parsed }];
};

export const inferValueType = (values = []) => {
    const present = values.filter((value) => value !== null && value !== undefined && String(value).trim() !== "");
    if (!present.length) return "empty";
    if (present.every((value) => typeof value === "boolean" || /^(true|false)$/i.test(String(value)))) return "boolean";
    if (present.every((value) => Number.isFinite(Number(String(value).replace(/,/g, "."))))) return "number";
    if (present.every((value) => !Number.isNaN(Date.parse(String(value))))) return "date";
    return "text";
};

export const profileRows = (rows = []) => {
    const columns = Array.from(new Set(rows.flatMap((row) => Object.keys(row || {}))));
    return columns.map((name) => {
        const values = rows.map((row) => row?.[name]);
        const type = inferValueType(values);
        const missing = values.filter((value) => value === null || value === undefined || String(value).trim() === "").length;
        const present = values.filter((value) => value !== null && value !== undefined && String(value).trim() !== "");
        const unique = new Set(present.map((value) => String(value))).size;
        let min = null; let max = null; let mean = null;
        if (type === "number") {
            const numbers = present.map((value) => Number(String(value).replace(/,/g, "."))).filter(Number.isFinite);
            min = numbers.length ? Math.min(...numbers) : null;
            max = numbers.length ? Math.max(...numbers) : null;
            mean = numbers.length ? numbers.reduce((sum, value) => sum + value, 0) / numbers.length : null;
        }
        return { name, type, missing, unique, min, max, mean };
    });
};

export const cleanRows = (rows = [], options = {}) => {
    const nullTokens = new Set(["null", "undefined", "n/a", "na", "nan", "-"]);
    let output = rows.map((row) => Object.fromEntries(Object.entries(row || {}).map(([key, raw]) => {
        let value = raw;
        if (typeof value === "string" && options.trimStrings) value = value.trim();
        if (options.normalizeNulls && typeof value === "string" && nullTokens.has(value.trim().toLowerCase())) value = "";
        return [options.trimHeaders ? key.trim() : key, value];
    })));
    if (options.dropEmptyRows) output = output.filter((row) => Object.values(row).some((value) => value !== null && value !== undefined && String(value).trim() !== ""));
    if (options.dropDuplicates) {
        const seen = new Set();
        output = output.filter((row) => {
            const key = JSON.stringify(row);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }
    return output;
};

const percentile = (sorted, fraction) => {
    if (!sorted.length) return 0;
    const index = (sorted.length - 1) * fraction;
    const lower = Math.floor(index); const upper = Math.ceil(index);
    if (lower === upper) return sorted[lower];
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
};

export const numericStatistics = (values = []) => {
    const numbers = values.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    if (!numbers.length) return null;
    const count = numbers.length;
    const sum = numbers.reduce((acc, value) => acc + value, 0);
    const mean = sum / count;
    const median = percentile(numbers, .5);
    const q1 = percentile(numbers, .25);
    const q3 = percentile(numbers, .75);
    const variance = numbers.reduce((acc, value) => acc + ((value - mean) ** 2), 0) / count;
    const stdDev = Math.sqrt(variance);
    const iqr = q3 - q1;
    const lowerFence = q1 - 1.5 * iqr;
    const upperFence = q3 + 1.5 * iqr;
    return {
        count, sum, mean, median, min: numbers[0], max: numbers[numbers.length - 1], q1, q3, variance, stdDev,
        outliers: numbers.filter((value) => value < lowerFence || value > upperFence).length,
    };
};
